import { Component } from '@theme/component';

/**
 * Snug Whiskey Fest — Customer-Facing Venue Map
 *
 * Fetches venue + event data from the snug-api-pro endpoints, renders an
 * interactive Leaflet map with grouped venue pins, a detail panel/bottom-sheet,
 * a venue strip, and day-of-week filtering.
 */
class SwfVenueMapComponent extends Component {
  #map = null;
  #venues = [];
  #events = [];
  #markers = new Map(); // venueKey -> { marker, venueData }
  #activeVenueKey = null;
  #activeDay = 'all';
  #isMobile = false;
  #resizeObserver = null;
  #onWinResize = null;

  connectedCallback() {
    super.connectedCallback();
    this.#isMobile = window.matchMedia('(max-width: 749px)').matches;
    window.matchMedia('(max-width: 749px)').addEventListener('change', (e) => {
      this.#isMobile = e.matches;
      if (this.#activeVenueKey) this.#renderVenueDetail(this.#activeVenueKey);
      // Re-measure when layout mode switches
      this.#scheduleInvalidate();
    });
    this.#ensureLeaflet().then(() => this.#loadData()).catch((err) => {
      console.error('[venue-map] Leaflet failed to load:', err);
      if (this.refs.loading) {
        this.refs.loading.innerHTML =
          '<span style="color:#b91c1c">Map library failed to load. Please refresh the page or check your connection.</span>';
      }
    });
  }

  /**
   * Ensure Leaflet (window.L) is available. The section template loads it via
   * a <script> tag from unpkg, but on slow/blocked mobile connections that
   * script may not be ready (or may have failed) by the time this component
   * upgrades. Poll briefly, then inject our own copy as a fallback.
   */
  #ensureLeaflet() {
    if (typeof L !== 'undefined') return Promise.resolve();

    return new Promise((resolve, reject) => {
      const start = Date.now();
      const POLL_MS = 50;
      const POLL_TIMEOUT_MS = 2500;

      const poll = () => {
        if (typeof L !== 'undefined') return resolve();
        if (Date.now() - start > POLL_TIMEOUT_MS) return injectFallback();
        setTimeout(poll, POLL_MS);
      };

      const injectFallback = () => {
        if (document.querySelector('script[data-swf-leaflet-fallback]')) {
          return reject(new Error('Leaflet fallback script already attempted but L is undefined'));
        }
        // Make sure the CSS is present too — section template loads it but be safe.
        if (!document.querySelector('link[href*="leaflet@1.9.4"]')) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
          link.crossOrigin = '';
          document.head.appendChild(link);
        }
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.crossOrigin = '';
        script.dataset.swfLeafletFallback = '1';
        script.onload = () => (typeof L !== 'undefined' ? resolve() : reject(new Error('Leaflet script loaded but L undefined')));
        script.onerror = () => reject(new Error('Leaflet fallback script failed to load'));
        document.head.appendChild(script);
      };

      poll();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }
    if (this.#onWinResize) {
      window.removeEventListener('resize', this.#onWinResize);
      window.removeEventListener('orientationchange', this.#onWinResize);
      window.removeEventListener('load', this.#onWinResize);
      this.#onWinResize = null;
    }
    if (this.#map) {
      this.#map.remove();
      this.#map = null;
    }
  }

  // ── Data loading ──────────────────────────────────────────────

  #loadData() {
    try {
      const venueScript = this.querySelector('script[data-venues]');
      const eventScript = this.querySelector('script[data-events]');

      this.#venues = venueScript ? JSON.parse(venueScript.textContent) : [];
      this.#events = eventScript ? JSON.parse(eventScript.textContent) : [];

      this.refs.loading?.setAttribute('hidden', '');
      this.#initMap();
      this.#renderSidebar();
    } catch (err) {
      console.error('[venue-map] Failed to load data:', err);
      if (this.refs.loading) {
        this.refs.loading.innerHTML =
          '<span style="color:#b91c1c">Unable to load venue data. Please refresh the page.</span>';
      }
    }
  }

  // ── Map initialisation ────────────────────────────────────────

  #initMap() {
    const mapEl = this.refs.map;
    if (!mapEl) return;
    if (typeof L === 'undefined') {
      console.error('[venue-map] Leaflet (window.L) not available at initMap');
      return;
    }

    this.#map = L.map(mapEl, {
      scrollWheelZoom: false,
      attributionControl: true,
      zoomControl: false,
    }).setView([39.7720, -86.0900], 14);

    L.control.zoom({ position: 'bottomright' }).addTo(this.#map);

    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://carto.com/">CARTO</a> &copy; <a href="https://openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(this.#map);

    this.#addMarkers();

    // Leaflet measures the container at init. On mobile the flex layout can
    // still be settling (iOS address bar, late font/image load, orientation
    // change), so invalidate multiple times and keep observing the container.
    this.#scheduleInvalidate();

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => this.#scheduleInvalidate());
      this.#resizeObserver.observe(mapEl);
    }

    this.#onWinResize = () => this.#scheduleInvalidate();
    window.addEventListener('resize', this.#onWinResize, { passive: true });
    window.addEventListener('orientationchange', this.#onWinResize, { passive: true });
    window.addEventListener('load', this.#onWinResize, { once: true });
  }

  #scheduleInvalidate() {
    if (!this.#map) return;
    const run = () => {
      if (!this.#map) return;
      this.#map.invalidateSize({ animate: false });
      // After layout snaps, re-fit bounds if we're showing all venues
      if (!this.#activeVenueKey && this.#markers.size > 1) {
        const allMarkers = [...this.#markers.values()].map((m) => m.marker);
        this.#map.fitBounds(L.featureGroup(allMarkers).getBounds().pad(0.15), { animate: false });
      }
    };
    requestAnimationFrame(() => {
      run();
      setTimeout(run, 150);
      setTimeout(run, 500);
    });
  }

  // ── Markers ───────────────────────────────────────────────────

  #groupVenues() {
    // Group venues by normalized display name so sub-venues (e.g. Strangebird + Tasting Room + Patio)
    // collapse into a single pin/card. The group's pin uses the first venue's coordinates;
    // each sub-venue's address is preserved in group.venues for multi-address display.
    const grouped = new Map();
    for (const venue of this.#venues) {
      const lat = parseFloat(venue.map_lat);
      const lng = parseFloat(venue.map_lng);
      if (isNaN(lat) || isNaN(lng)) continue;

      const key = this.#venueLabelKey(venue.name);
      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          lat,
          lng,
          venues: [],
          primaryName: venue.name,
          image: this.#extractImageUrl(venue.featured_image),
        });
      }
      const g = grouped.get(key);
      g.venues.push(venue);
      // Prefer the first venue that has a logo image
      if (!g.image) g.image = this.#extractImageUrl(venue.featured_image);
    }
    return grouped;
  }

  #addMarkers() {
    const grouped = this.#groupVenues();

    for (const [key, group] of grouped) {
      const icon = this.#createIcon(group);
      const marker = L.marker([group.lat, group.lng], {
        icon,
        keyboard: true,
        title: group.primaryName,
      }).addTo(this.#map);

      marker.on('click', () => this.#selectVenue(key));
      marker.on('touchstart', () => {
        const el = marker.getElement()?.querySelector('.swf-vm-pin');
        if (!el) return;
        el.classList.add('swf-vm-pin--touched');
        setTimeout(() => el.classList.remove('swf-vm-pin--touched'), 2000);
      });
      marker.on('keydown', (e) => {
        if (e.originalEvent.key === 'Enter' || e.originalEvent.key === ' ') {
          e.originalEvent.preventDefault();
          this.#selectVenue(key);
        }
      });

      this.#markers.set(key, { marker, group });
    }

    // Fit bounds
    if (grouped.size > 1) {
      const allMarkers = [...this.#markers.values()].map((m) => m.marker);
      this.#map.fitBounds(L.featureGroup(allMarkers).getBounds().pad(0.15));
    }
  }

  #createIcon(group, active = false, dimmed = false) {
    const name = group.primaryName || '';

    const classes = ['swf-vm-pin'];
    if (active) classes.push('swf-vm-pin--active');
    if (dimmed) classes.push('swf-vm-pin--dimmed');

    const html = `<div class="${classes.join(' ')}">
        <div class="swf-vm-pin__label">${this.#escapeHtml(name)}</div>
        <div class="swf-vm-pin__dot">🥃</div>
      </div>`;

    return L.divIcon({
      html,
      className: 'swf-vm-marker-wrapper',
      iconSize: null,
      iconAnchor: [20, 52],
    });
  }

  // ── Venue selection ───────────────────────────────────────────

  #selectVenue(key) {
    // Toggle off if clicking same venue
    if (this.#activeVenueKey === key) {
      this.#deselectVenue();
      return;
    }

    this.#activeVenueKey = key;
    this.#updateMarkerStates();
    this.#renderVenueDetail(key);
    this.#updateSidebarStates();

    // Pan map to venue
    const entry = this.#markers.get(key);
    if (entry) {
      this.#map?.setView([entry.group.lat, entry.group.lng], 15, { animate: true });
    }
  }

  #deselectVenue() {
    this.#activeVenueKey = null;
    this.#updateMarkerStates();
    this.#updateSidebarStates();
    this.#hidePanel();

    // Reset map view
    if (this.#markers.size > 1) {
      const allMarkers = [...this.#markers.values()].map((m) => m.marker);
      this.#map?.fitBounds(L.featureGroup(allMarkers).getBounds().pad(0.15));
    }
  }

  #updateMarkerStates() {
    for (const [key, { marker, group }] of this.#markers) {
      const isActive = key === this.#activeVenueKey;
      const isDimmed = this.#isVenueDimmed(group);
      const icon = this.#createIcon(group, isActive, isDimmed);
      marker.setIcon(icon);
      if (isActive) marker.setZIndexOffset(1000);
      else marker.setZIndexOffset(0);
    }
  }

  // ── Events matching ───────────────────────────────────────────

  /** Aligns Liquid swf-venue-display-name: strip " (…)", " Large/Small Room",
   *  " Tasting Room", " Patio", and anything after " Room " so sub-venues collapse. */
  #venueLabelKey(s) {
    let t = (s || '').trim();
    const paren = t.indexOf(' (');
    if (paren >= 0) t = t.slice(0, paren).trim();
    const roomSpace = t.indexOf(' Room ');
    if (roomSpace >= 0) t = t.slice(0, roomSpace).trim();
    t = t.replace(/ Large Room$/i, '')
         .replace(/ Small Room$/i, '')
         .replace(/ Tasting Room$/i, '')
         .replace(/ Patio$/i, '')
         .trim();
    return t.toLowerCase();
  }

  #getEventsForGroup(group) {
    const keys = new Set(group.venues.map((v) => this.#venueLabelKey(v.name)));
    return this.#events.filter((evt) => keys.has(this.#venueLabelKey(evt.venueName)));
  }

  #filterEventsByDay(events) {
    if (this.#activeDay === 'all') return events;
    return events.filter((evt) => evt.eventDate === this.#activeDay);
  }

  #isVenueDimmed(group) {
    if (this.#activeDay === 'all') return false;
    const events = this.#getEventsForGroup(group);
    return !events.some((evt) => evt.eventDate === this.#activeDay);
  }

  #getDayClass(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T12:00:00');
    const day = d.getDay();
    if (day === 5) return 'fri';
    if (day === 6) return 'sat';
    if (day === 0) return 'sun';
    return '';
  }

  #formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }

  #formatPrice(price) {
    if (price == null || price === '') return '';
    const num = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(num)) return price;
    return `$${num.toFixed(2)}`;
  }

  // ── Panel rendering ───────────────────────────────────────────

  #renderVenueDetail(key) {
    const entry = this.#markers.get(key);
    if (!entry) return;

    const { group } = entry;
    const allEvents = this.#getEventsForGroup(group);
    const filteredEvents = this.#filterEventsByDay(allEvents);

    // Build venue header
    const primaryVenue = group.venues[0];
    const subVenueNames = group.venues.length > 1
      ? group.venues.map((v) => this.#escapeHtml(v.raw_name || v.name)).join(' &bull; ')
      : '';

    const logoHtml = group.image
      ? `<img class="swf-venue-map__panel-logo" src="${this.#escapeHtml(group.image)}" alt="${this.#escapeHtml(group.primaryName)}">`
      : `<div class="swf-venue-map__panel-logo--letter">${(group.primaryName || '?').charAt(0).toUpperCase()}</div>`;

    const websiteHtml = primaryVenue.website_url
      ? `<a class="swf-venue-map__panel-link" href="${this.#escapeHtml(primaryVenue.website_url)}" target="_blank" rel="noopener">More Info &rarr;</a>`
      : '';

    const descRaw = primaryVenue.description || '';
    const descHtml = descRaw
      ? `<div class="swf-venue-map__panel-desc">${this.#sanitizeHtml(descRaw)}</div>`
      : '';

    // Build event cards
    let eventsHtml = '';
    if (filteredEvents.length > 0) {
      eventsHtml = filteredEvents
        .sort((a, b) => {
          const dateCompare = (a.eventDate || '').localeCompare(b.eventDate || '');
          if (dateCompare !== 0) return dateCompare;
          return (a.eventTime || '').localeCompare(b.eventTime || '');
        })
        .map((evt) => {
          const dayClass = this.#getDayClass(evt.eventDate);
          const isSoldOut = evt.availableForSale === false ||
            (evt.totalInventory > 0 && evt.totalSold >= evt.totalInventory);
          const priceNum = typeof evt.price === 'string' ? parseFloat(evt.price) : evt.price;
          const isFree = !isNaN(priceNum) && priceNum === 0;

          let priceHtml = '';
          let badgeHtml = '';
          if (isSoldOut) {
            badgeHtml = `<span class="swf-venue-map__panel-event-badge swf-venue-map__panel-event-badge--sold-out">Sold Out</span>`;
            priceHtml = isFree ? '' : `<span class="swf-venue-map__panel-event-price">${this.#formatPrice(evt.price)}</span>`;
          } else if (isFree) {
            priceHtml = `<span class="swf-venue-map__panel-event-price swf-venue-map__panel-event-price--free">No Ticket Required</span>`;
          } else {
            priceHtml = `<span class="swf-venue-map__panel-event-price">${this.#formatPrice(evt.price)}</span>`;
          }

          const productUrl = `/products/${this.#escapeHtml(evt.handle)}`;

          return `
            <a href="${productUrl}" class="swf-venue-map__panel-event">
              <p class="swf-venue-map__panel-event-title">${this.#escapeHtml(evt.title)}</p>
              <p class="swf-venue-map__panel-event-meta">
                ${dayClass ? `<span class="swf-venue-map__panel-event-day swf-venue-map__panel-event-day--${dayClass}"></span>` : ''}
                <span>${this.#formatDate(evt.eventDate)}</span>
                ${evt.eventTime ? `<span>${this.#escapeHtml(evt.eventTime)}${evt.eventEndTime ? '–' + this.#escapeHtml(evt.eventEndTime) : ''}</span>` : ''}
                ${priceHtml}
                ${badgeHtml}
              </p>
            </a>`;
        })
        .join('');
    } else {
      eventsHtml = '<p class="swf-venue-map__panel-no-events">Events will be updated on website soon.</p>';
    }

    // Build address block — one line per unique sub-venue address.
    const seenAddrs = new Set();
    const addrLines = [];
    for (const v of group.venues) {
      const line = `${v.address || ''}${v.city ? ', ' + v.city : ''}`.trim();
      if (!line || seenAddrs.has(line)) continue;
      seenAddrs.add(line);
      const label = group.venues.length > 1 ? (v.raw_name || v.name) : '';
      addrLines.push(
        `<p class="swf-venue-map__panel-address">` +
          (label ? `<strong style="color:var(--color-foreground,#1a1a1a)">${this.#escapeHtml(label)}</strong><br>` : '') +
          this.#escapeHtml(line) +
        `</p>`
      );
    }
    const addressHtml = addrLines.join('');

    const html = `
      ${logoHtml}
      <h3 class="swf-venue-map__panel-name">${this.#escapeHtml(group.primaryName)}</h3>
      ${addressHtml}
      ${descHtml}
      ${websiteHtml}
      <h4 class="swf-venue-map__panel-events-title">Events (${filteredEvents.length})</h4>
      ${eventsHtml}
    `;

    if (this.#isMobile) {
      this.#showSheet(html);
    } else {
      this.#showPanel(html);
    }
  }

  // ── Desktop panel ─────────────────────────────────────────────

  #showPanel(html) {
    const panel = this.refs.panel;
    const content = this.refs.panelContent;
    if (!panel || !content) return;

    content.innerHTML = html;
    panel.removeAttribute('hidden');

    // Invalidate map size after panel animation
    setTimeout(() => this.#map?.invalidateSize({ animate: true }), 320);
  }

  #hidePanel() {
    const panel = this.refs.panel;
    if (panel) panel.setAttribute('hidden', '');

    const sheet = this.refs.sheet;
    if (sheet) sheet.setAttribute('hidden', '');

    // Reset mobile detail to empty state
    const mobileDetail = this.refs.mobileDetail;
    if (mobileDetail) {
      mobileDetail.innerHTML = `
        <div class="swf-venue-map__mobile-detail-empty">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
          <span>Tap a venue on the map or above to see details</span>
        </div>`;
    }

    setTimeout(() => this.#map?.invalidateSize({ animate: true }), 320);
  }

  closePanel() {
    this.#deselectVenue();
  }

  zoomOut() {
    if (!this.#map) return;
    const allMarkers = [...this.#markers.values()].map((m) => m.marker);
    if (allMarkers.length > 1) {
      this.#map.fitBounds(L.featureGroup(allMarkers).getBounds().pad(0.15), { animate: true });
    } else if (allMarkers.length === 1) {
      const [m] = allMarkers;
      this.#map.setView(m.getLatLng(), 14, { animate: true });
    } else {
      this.#map.setView([39.7720, -86.0900], 14, { animate: true });
    }
  }

  navigateVenue(event) {
    const btn = event.target.closest('.swf-venue-map__panel-nav-btn');
    if (!btn) return;
    const dir = parseInt(btn.dataset.dir, 10);
    const keys = [...this.#markers.keys()];
    if (keys.length === 0) return;
    const currentIndex = this.#activeVenueKey ? keys.indexOf(this.#activeVenueKey) : 0;
    const nextIndex = (currentIndex + dir + keys.length) % keys.length;
    this.#selectVenue(keys[nextIndex]);
  }

  // ── Mobile bottom sheet / panel ────────────────────────────────

  #showSheet(html) {
    // Use the new inline mobile detail panel if available
    const mobileDetail = this.refs.mobileDetail;
    if (mobileDetail) {
      mobileDetail.innerHTML = html;
      mobileDetail.scrollTop = 0;
      return;
    }

    // Fallback to legacy bottom sheet
    const sheet = this.refs.sheet;
    const content = this.refs.sheetContent;
    if (!sheet || !content) return;

    content.innerHTML = html;
    sheet.removeAttribute('hidden');
    sheet.classList.remove('swf-venue-map__sheet--collapsed');
  }

  toggleSheet() {
    const sheet = this.refs.sheet;
    if (!sheet) return;
    sheet.classList.toggle('swf-venue-map__sheet--collapsed');
  }

  // ── Venue sidebar ─────────────────────────────────────────────

  #renderSidebar() {
    const sidebar = this.refs.sidebar;
    if (!sidebar) return;

    const grouped = this.#groupVenues();
    let html = '';

    for (const [key, group] of grouped) {
      const eventCount = this.#getEventsForGroup(group).length;
      const isDimmed = eventCount === 0;

      const logoHtml = group.image
        ? `<img class="swf-venue-map__sb-logo" src="${this.#escapeHtml(group.image)}" alt="${this.#escapeHtml(group.primaryName)}" loading="lazy">`
        : `<span class="swf-venue-map__sb-initial">${(group.primaryName || '?').charAt(0).toUpperCase()}</span>`;

      const classes = ['swf-venue-map__sb-card'];
      if (isDimmed) classes.push('swf-venue-map__sb-card--dimmed');

      html += `
        <button
          type="button"
          class="${classes.join(' ')}"
          data-venue-key="${this.#escapeHtml(key)}"
          on:click="/selectVenueFromSidebar"
          role="listitem"
          tabindex="0"
          aria-label="${this.#escapeHtml(group.primaryName)} — ${eventCount} event${eventCount !== 1 ? 's' : ''}"
        >
          ${logoHtml}
          <span class="swf-venue-map__sb-name">${this.#escapeHtml(group.primaryName)}</span>
        </button>`;
    }

    sidebar.innerHTML = html;

    // Also populate mobile venue strip (uses same click handler via event delegation)
    const mobileSidebar = this.refs.mobileSidebar;
    if (mobileSidebar) {
      mobileSidebar.innerHTML = html;
    }

    // Update badge venue count
    const badgeCount = this.refs.badgeVenueCount;
    if (badgeCount) {
      badgeCount.textContent = `${grouped.size} venue${grouped.size !== 1 ? 's' : ''}`;
    }
  }

  #updateSidebarStates() {
    for (const container of [this.refs.sidebar, this.refs.mobileSidebar]) {
      if (!container) continue;
      for (const card of container.querySelectorAll('.swf-venue-map__sb-card')) {
        const key = card.dataset.venueKey;
        card.classList.toggle('swf-venue-map__sb-card--active', key === this.#activeVenueKey);
      }
    }
  }

  selectVenueFromSidebar(event) {
    const btn = event.target.closest('.swf-venue-map__sb-card');
    if (!btn) return;
    const key = btn.dataset.venueKey;
    if (key) this.#selectVenue(key);
  }

  // ── Day filtering ─────────────────────────────────────────────

  filterByDay(event) {
    const btn = event.target.closest('.swf-venue-map__filter-btn');
    if (!btn) return;

    const day = btn.dataset.day;
    this.#activeDay = day;

    // Update button states
    const filters = this.refs.filters;
    if (filters) {
      for (const b of filters.querySelectorAll('.swf-venue-map__filter-btn')) {
        const isActive = b.dataset.day === day;
        b.classList.toggle('swf-venue-map__filter-btn--active', isActive);
        b.setAttribute('aria-pressed', String(isActive));
      }
    }

    // Update marker dimming
    this.#updateMarkerStates();

    // Update sidebar dimming
    this.#updateSidebarDimming();

    // Re-render venue detail if a venue is selected
    if (this.#activeVenueKey) {
      this.#renderVenueDetail(this.#activeVenueKey);
    }
  }

  #updateSidebarDimming() {
    for (const container of [this.refs.sidebar, this.refs.mobileSidebar]) {
      if (!container) continue;
      for (const card of container.querySelectorAll('.swf-venue-map__sb-card')) {
        const key = card.dataset.venueKey;
        const entry = this.#markers.get(key);
        if (!entry) continue;

        const isDimmed = this.#isVenueDimmed(entry.group);
        card.classList.toggle('swf-venue-map__sb-card--dimmed', isDimmed);
      }
    }
  }

  // ── Utilities ─────────────────────────────────────────────────

  /**
   * Extract plain text from a rich_text_field value (JSON object)
   * or return the string as-is if it's already a string (HTML from metafield).
   */
  #extractText(value) {
    if (!value) return '';
    let raw = '';
    if (typeof value === 'string') {
      raw = value;
    } else if (typeof value === 'object' && value.children) {
      raw = value.children
        .map((block) =>
          (block.children || []).map((child) => child.value || '').join('')
        )
        .filter(Boolean)
        .join(' ');
    } else {
      raw = String(value);
    }
    // If the string contains HTML tags, strip them and decode entities
    if (raw.includes('<')) {
      const div = document.createElement('div');
      div.innerHTML = raw;
      return div.textContent || '';
    }
    return raw;
  }

  /**
   * Extract URL from a file_reference field value.
   * The API returns { id, url, alt } for file_reference fields.
   */
  #extractImageUrl(value) {
    if (!value) return null;
    if (typeof value === 'string') return value;
    return value.url || null;
  }

  #escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = String(str);
    return div.innerHTML;
  }

  /** Allow only safe inline/block HTML from Shopify rich text metafields. */
  #sanitizeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.innerHTML = String(str);
    const allowed = new Set([
      'P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'A', 'UL', 'OL', 'LI',
      'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'SPAN', 'BLOCKQUOTE',
    ]);
    const walk = (node) => {
      for (const child of [...node.childNodes]) {
        if (child.nodeType === 1) {
          if (!allowed.has(child.tagName)) {
            child.replaceWith(...child.childNodes);
          } else {
            // Strip all attributes except href on anchors
            for (const attr of [...child.attributes]) {
              if (!(child.tagName === 'A' && attr.name === 'href')) {
                child.removeAttribute(attr.name);
              }
            }
            walk(child);
          }
        }
      }
    };
    walk(div);
    return div.innerHTML;
  }
}

if (!customElements.get('swf-venue-map-component')) {
  customElements.define('swf-venue-map-component', SwfVenueMapComponent);
}
